import { configureStore } from '@reduxjs/toolkit';
import authReducer from './Features/Authentication/authSlice';
import alertReducer from './Features/Alerts/alertSlice'; // Import alertSlice reducer
import notificationReducer from './Features/Notifications/notificationSlice';
import taskReducer from './Features/Tasks/taskSlice';
import ticketReducer from './Features/Tickets/ticketSlice';
import broadcastReducer from './Features/Broadcasts/broadcastSlice';
import realtimeReducer from './Features/Realtime/realtimeSlice';
import { apiSlice } from './Features/API/apiSlice'; // Legacy API slice (to be deprecated)
import { authApiSlice } from './Features/API/authApiSlice';
import { rbacApiSlice } from './Features/API/rbacApiSlice';
import { aiApiSlice } from './Features/API/aiApiSlice';
import { testEnvironmentApiSlice } from './Features/API/testEnvironmentApiSlice';

// Portal-specific API slices
import { bureauApiSlice } from './portals/bureau/api/bureauApiSlice';
import { adminApiSlice } from './portals/admin/api/adminApiSlice';
import { consolidationApiSlice } from './portals/admin/api/consolidationApiSlice';
import { groupLinksApi } from './portals/admin/api/groupLinksApiSlice';
import { groupManagementApiSlice } from './portals/admin/api/groupManagementApiSlice';
import { clientApiSlice } from './portals/client/api/clientApiSlice';
import { clientEventsApiSlice } from './portals/client/api/clientEventsApiSlice';
import { chatApiSlice } from './portals/client/api/chatApiSlice';
import { superAdminApiSlice } from './portals/super-admin/api/superAdminApiSlice';
import { tiplockApiSlice } from './portals/tiplock/api/tiplockApiSlice';
import { salesApiSlice } from './portals/sales/api/salesApiSlice';
import { technicianApiSlice } from './portals/technician/api/technicianApiSlice';

// Shared notification system API slice
import { notificationApiSlice } from './shared/notifications/api/notificationApiSlice';

// Shared driver–vehicle authorisation API slice (client edits, admin reads)
import { driverAuthorizationApiSlice } from './shared/driverAuthorization/api/driverAuthorizationApiSlice';

// Portal-specific feature slices
import clientDashboardReducer from './portals/client/features/dashboardSlice';

export const store = configureStore({
  reducer: {
    auth: authReducer,
    alerts: alertReducer,
    notifications: notificationReducer,
    tasks: taskReducer,
    tickets: ticketReducer,
    broadcasts: broadcastReducer,
    realtime: realtimeReducer,
    clientDashboard: clientDashboardReducer,

    // Legacy API slice (to be gradually replaced)
    [apiSlice.reducerPath]: apiSlice.reducer,
    [authApiSlice.reducerPath]: authApiSlice.reducer,
    [rbacApiSlice.reducerPath]: rbacApiSlice.reducer,
    [aiApiSlice.reducerPath]: aiApiSlice.reducer,
    [testEnvironmentApiSlice.reducerPath]: testEnvironmentApiSlice.reducer,

    // Portal-specific API slices
    [bureauApiSlice.reducerPath]: bureauApiSlice.reducer,
    [adminApiSlice.reducerPath]: adminApiSlice.reducer,
    [consolidationApiSlice.reducerPath]: consolidationApiSlice.reducer,
    [groupLinksApi.reducerPath]: groupLinksApi.reducer,
    [groupManagementApiSlice.reducerPath]: groupManagementApiSlice.reducer,
    [clientApiSlice.reducerPath]: clientApiSlice.reducer,
    [clientEventsApiSlice.reducerPath]: clientEventsApiSlice.reducer,
    [chatApiSlice.reducerPath]: chatApiSlice.reducer,
    [superAdminApiSlice.reducerPath]: superAdminApiSlice.reducer,
    [tiplockApiSlice.reducerPath]: tiplockApiSlice.reducer,
    [salesApiSlice.reducerPath]: salesApiSlice.reducer,
    [technicianApiSlice.reducerPath]: technicianApiSlice.reducer,

    // Shared notification system
    [notificationApiSlice.reducerPath]: notificationApiSlice.reducer,

    // Shared driver–vehicle authorisation
    [driverAuthorizationApiSlice.reducerPath]: driverAuthorizationApiSlice.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // A file export resolves to a Blob, which is binary by definition and
      // cannot be serialised. It is read once, saved to disk, and the mutation
      // is reset immediately afterwards, so it never lingers in the store.
      serializableCheck: {
        // Naming these replaces Redux Toolkit's own defaults, so the defaults
        // ('meta.arg', 'payload.timestamp') are repeated here, along with the
        // Request/Response objects RTK Query attaches to every query's meta.
        ignoredActionPaths: [
          'meta.arg',
          'payload.timestamp',
          'meta.baseQueryMeta.request',
          'meta.baseQueryMeta.response',
          'payload.blob',
        ],
        // One entry per slice with a file-export mutation: the legacy api slice
        // (Comms) and the client events slice.
        ignoredPaths: [/^(api|clientEventsApi)\.mutations\..*\.data\.blob$/],
      },
    }).concat(
      // Legacy API middleware
      apiSlice.middleware,
      authApiSlice.middleware,
      rbacApiSlice.middleware,
      aiApiSlice.middleware,
      testEnvironmentApiSlice.middleware,

      // Portal-specific API middleware
      bureauApiSlice.middleware,
      adminApiSlice.middleware,
      consolidationApiSlice.middleware,
      groupLinksApi.middleware,
      groupManagementApiSlice.middleware,
      clientApiSlice.middleware,
      clientEventsApiSlice.middleware,
      chatApiSlice.middleware,
      superAdminApiSlice.middleware,
      tiplockApiSlice.middleware,
      salesApiSlice.middleware,
      technicianApiSlice.middleware,
      notificationApiSlice.middleware,
      driverAuthorizationApiSlice.middleware
    ),
});

export default store;
